import { createAction, createReducer } from "@reduxjs/toolkit"
import { api } from "./helpers/api"
import { find, omit } from "lodash"

import { LOADING_INITIAL_STATE, setFailed, setReceived, setRequested } from "modules/loader-watchers/helpers/loading"

// ACTIONS
// Action for adding a new expense category
const addExpenseCategory = createAction("accounting/addExpenseCategory")

const requestedLabels = createAction("accounting/requestedLabels")
const receivedLabels = createAction("accounting/receivedLabels")
const failedLabels = createAction("accounting/failedLabels")
const createdLabel = createAction("accounting/createdLabel")
const updatedLabel = createAction("accounting/updatedLabel")
const deletedLabel = createAction("accounting/deletedLabel")

const requested = createAction("accounting/requested")
const failed = createAction("accounting/failed")
const received = createAction("accounting/received")
const cleaned = createAction("accounting/cleaned")

const receivedOverview = createAction("accounting/receivedOverview")

const requestedExpense = createAction("accounting/requestedExpense")
const receivedExpense = createAction("accounting/receivedExpense")
const failedExpense = createAction("accounting/failedExpense")
const createdExpense = createAction("accounting/createdExpense")
const updatedExpense = createAction("accounting/updatedExpense")
const cleanedExpense = createAction("accounting/cleanedExpense")
const deletedExpense = createAction("accounting/deletedExpense")

const receiptParseRequested = createAction("accounting/receiptParseRequested")
const receiptParsed = createAction("accounting/receiptParsed")

const requestedTripList = createAction("accounting/requestedTripList")
const receivedTripList = createAction("accounting/receivedTripList")
const failedTripList = createAction("accounting/failedTripList")

const initialState = {
  ...LOADING_INITIAL_STATE,
  receiptParsing: false,
  items: [],
  metadata: {
    bookings_stats: {},
    booking_client_stats: {},
    expense_categories: [],
    expense_category_stats: {},
    expenses_stats: {},
    incomes_stats: {},
    incomes_per_day_stats: {},
    profit_stats: {},
    labels: []
  },
  tripList: [],
  expense: {
    ...LOADING_INITIAL_STATE,
    id: null,
    guide_id: null,
    amount: 0,
    purchase_date: "",
    recurring_type: "",
    recurring: false,
    notes: "",
    created_at: "",
    updated_at: "",
    receipts: [],
    category: {
      id: null,
      title: "",
      slug: ""
    },
    merchant: {
      id: null,
      name: "",
      guide_id: null
    }
  }
}

// REDUCER
const accountingReducer = createReducer(initialState, {
  [addExpenseCategory.type]: (state, action) => {
    // Support both response.data and response.data.data
    const category = action?.payload?.data || action?.payload
    if (category?.id && category.name) {
      const {id, name, slug} = category
      state?.metadata?.expense_categories?.push({
        id,
        name,
        slug
      })
    }
  },
  [requested.type]: (state) => {
    setRequested(state)
    state.metadata.pagination = {}
  },
  [received.type]: (state, action) => {
    setReceived(state)
    state.items = action.payload.data
    const incoming = action.payload.metadata || {}
    Object.assign(state.metadata, incoming)
    if (incoming.pagination) state.metadata.pagination = incoming.pagination
  },
  [failed.type]: (state) => {
    setFailed(state)
  },
  [cleaned.type]: () => initialState,
  [receivedOverview.type]: (state, action) => {
    setReceived(state)
    Object.assign(state.metadata, action.payload.data)
    Object.assign(state.metadata, action.payload.metadata)
  },
  [requestedExpense.type]: (state) => {
    setRequested(state.expense)
  },
  [receivedExpense.type]: (state, action) => {
    setReceived(state.expense)
    Object.assign(state.expense, action.payload)
  },
  [createdExpense.type]: (state, { payload }) => {
    setReceived(state.expense)
    state.items = [payload?.metadata?.formatted_expense, ...state.items]
    Object.assign(state.expense, payload?.data)
    Object.assign(state.metadata, omit(payload?.metadata, "formatted_expense"))
  },
  [updatedExpense.type]: (state, { payload }) => {
    setReceived(state.expense)
    const expense = find(state.items, ["id", payload.data?.id]) || {}
    Object.assign(expense, payload.metadata.formatted_expense)
    Object.assign(state.expense, payload.data)
    Object.assign(state.metadata, omit(payload.metadata, "formatted_expense"))
  },
  [failedExpense.type]: (state) => {
    setFailed(state.expense)
    state.receiptParsing = false
  },
  [cleanedExpense.type]: (state) => {
    state.expense = initialState.expense
  },
  [receiptParseRequested.type]: (state) => {
    state.receiptParsing = true
  },
  [receiptParsed.type]: (state) => {
    state.receiptParsing = false
  },
  [receivedTripList.type]: (state, action) => {
    state.tripList = action.payload?.data || action.payload || []
  },
  [requestedLabels.type]: (state) => {
    state.labelsLoading = true
  },
  [receivedLabels.type]: (state, action) => {
    state.labelsLoading = false
    state.metadata.labels = action.payload?.data || action.payload || []
  },
  [failedLabels.type]: (state) => {
    state.labelsLoading = false
  },
  [createdLabel.type]: (state, action) => {
    const label = action.payload?.data || action.payload
    if (label?.id && label.name) {
      state.metadata.labels.push(label)
    }
  },
  [updatedLabel.type]: (state, action) => {
    const label = action.payload?.data || action.payload
    if (label?.id) {
      const index = state.metadata.labels.findIndex((l) => l.id === label.id)
      if (index !== -1) state.metadata.labels[index] = label
    }
  },
  [deletedLabel.type]: (state, action) => {
    const id = action.payload?.id || action.payload
    state.metadata.labels = state.metadata.labels.filter((l) => l.id !== id)
  },
})

export default accountingReducer

//PUBLIC ACTIONS
export const addCategory = (data) =>
  api({
    url: "/guide/accounting/create_expense_category",
    method: "post",
    data,
    onSuccess: addExpenseCategory.type
  })
export const getGuideAccounting = (params) =>
  api({
    url: `/guide/accounting`,
    params,
    onStart: requested.type,
    onSuccess: received.type,
    onError: failed.type
  })

export const getGuideTipReport = (params) =>
  api({
    url: `/guide/tip_reports`,
    params,
    onStart: requested.type,
    onSuccess: received.type,
    onError: failed.type
  })

export const getGuideAccountingOverview = (params) =>
  api({
    url: `/guide/accounting/overview`,
    params,
    onStart: requested.type,
    onSuccess: receivedOverview.type,
    onError: failed.type
  })

export const getExpense = (id) =>
  api({
    url: `/guide/expenses/${id}`,
    onStart: requestedExpense.type,
    onSuccess: receivedExpense.type,
    onError: failedExpense.type
  })

export const createExpense = (data) =>
  api({
    url: "/guide/expenses",
    method: "post",
    data: data,
    onStart: requestedExpense.type,
    onSuccess: createdExpense.type,
    onError: failedExpense.type
  })

export const updateExpense = (id, data) =>
  api({
    url: `/guide/expenses/${id}`,
    method: "put",
    data: data,
    onStart: requestedExpense.type,
    onSuccess: updatedExpense.type,
    onError: failedExpense.type
  })

export const saveExpense = (id, data) => (id ? updateExpense(id, data) : createExpense(data))

export const deleteExpense = (id) =>
  api({
    url: `/guide/expenses/${id}`,
    method: "delete",
    onSuccess: deletedExpense.type
  })
export const parseExpenseReceipt = (data) =>
  api({
    url: "guide/expenses/parse_receipt",
    data,
    method: "post",
    onStart: receiptParseRequested.type,
    onSuccess: receiptParsed.type,
    onError: failedExpense.type
  })

export const checkDuplicateExpense = (params) =>
  api({
    url: "guide/expenses/check_duplicate",
    params,
    suppressErrorNotification: true
  })

export const cleanExpenses = () => (dispatch) => dispatch(cleaned())
export const cleanExpense = () => (dispatch) => dispatch(cleanedExpense())

export const getAccountingTripList = () =>
  api({
    url: "/guide/trips/trip_list",
    onStart: requestedTripList.type,
    onSuccess: receivedTripList.type,
    onError: failedTripList.type
  })

export const getLabels = () =>
  api({
    url: "/guide/labels",
    onStart: requestedLabels.type,
    onSuccess: receivedLabels.type,
    onError: failedLabels.type
  })

export const createLabel = (name) =>
  api({
    url: "/guide/labels",
    method: "post",
    data: { label: { name } },
    onSuccess: createdLabel.type,
    suppressErrorNotification: true
  })

export const updateLabel = (id, name) =>
  api({
    url: `/guide/labels/${id}`,
    method: "put",
    data: { label: { name } },
    onSuccess: updatedLabel.type,
    suppressErrorNotification: true
  })

export const deleteLabel = (id) =>
  api({
    url: `/guide/labels/${id}`,
    method: "delete",
    onSuccess: deletedLabel.type,
    onSuccessData: { id }
  })

